import { Type } from 'app/actions/form/reverse-mortgage/types';
import { RootState } from 'app/store/types';
import { createSelector } from 'reselect';

/**
 * hecmFailed/highTechFailed/helocFailed are tracked per side because the calcs are requested together
 * but can succeed or fail independently; a failed side's numbers in the form are stale and
 * untrustworthy. hecmError/highTechError are pricing-engine's own description of why a failed side
 * failed (e.g. no plan for this age/payment option), and are undefined when that side didn't fail or
 * sent no description. helocError is a fixed borrower-friendly message, because the home equity
 * endpoints' descriptions aren't written for borrowers.
 */
type ReverseMortgageState = {
  isPending       : boolean;
  hecmFailed      : boolean;
  highTechFailed  : boolean;
  helocFailed     : boolean;
  hecmError?      : string;
  highTechError?  : string;
  helocError?     : string;
};

const initialState: ReverseMortgageState = { isPending: false, hecmFailed: false, highTechFailed: false, helocFailed: false };

export const reverseMortgageReducer = (state = initialState, action): ReverseMortgageState => {
  switch (action.type) {
    case Type.AJAX_REVERSE_MORTGAGE_CALCS:
      return { ...state, isPending: true };

    // Success means at least one side came back; the payload says which (if any) did not.
    case Type.AJAX_REVERSE_MORTGAGE_CALCS_SUCCESS:
      return {
        isPending      : false,
        hecmFailed     : !!action.payload?.hecmFailed,
        highTechFailed : !!action.payload?.highTechFailed,
        helocFailed    : !!action.payload?.helocFailed,
        hecmError      : action.payload?.hecmError,
        highTechError  : action.payload?.highTechError,
        helocError     : action.payload?.helocError,
      };

    case Type.AJAX_REVERSE_MORTGAGE_CALCS_FAIL:
      return {
        isPending      : false,
        hecmFailed     : true,
        highTechFailed : true,
        helocFailed    : true,
        hecmError      : action.payload?.hecmError,
        highTechError  : action.payload?.highTechError,
        helocError     : action.payload?.helocError,
      };

    default:
      return state;
  }
};

export const reverseMortgageSelector = (state: RootState): ReverseMortgageState => state.reverseMortgage;
export const isReverseMortgageCalcsPending = createSelector([reverseMortgageSelector], (reverseMortgage) => reverseMortgage.isPending);
export const hasHecmCalcFailed = createSelector([reverseMortgageSelector], (reverseMortgage) => reverseMortgage.hecmFailed);
export const hasHighTechCalcFailed = createSelector([reverseMortgageSelector], (reverseMortgage) => reverseMortgage.highTechFailed);
export const getHecmCalcError = createSelector([reverseMortgageSelector], (reverseMortgage) => reverseMortgage.hecmError);
export const getHighTechCalcError = createSelector([reverseMortgageSelector], (reverseMortgage) => reverseMortgage.highTechError);
export const hasHelocCalcFailed = createSelector([reverseMortgageSelector], (reverseMortgage) => reverseMortgage.helocFailed);
export const getHelocCalcError = createSelector([reverseMortgageSelector], (reverseMortgage) => reverseMortgage.helocError);
